import React, { useState, useRef } from 'react';
import { FaPlus, FaTimes, FaImage } from 'react-icons/fa';
import { AgencyService } from '../../services/supabase/agency.service';
import { DEFAULT_LOGO_CONFIG } from '../../types/agency';

interface LogoUploadProps {
  agencyId: string;
  currentLogo?: string | null;
  onLogoUpdate: (logoUrl: string | null) => void;
  disabled?: boolean;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  agencyId,
  currentLogo,
  onLogoUpdate,
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentLogo || null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Valider le fichier
    const validation = AgencyService.validateLogoFile(file);
    if (!validation.isValid) {
      setError(validation.error || 'Erreur de validation du fichier');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      // Utiliser la méthode simple directe en base64
      console.log('🎯 Upload logo direct en base de données (sans Storage)...');
      const logoUrl = await AgencyService.uploadLogoDirect(agencyId, file);
      
      if (!logoUrl) {
        throw new Error('Erreur lors de l\'upload du logo');
      }

      // Le logo est déjà sauvegardé en base, plus besoin d'appeler updateAgencyLogo
      onLogoUpdate(logoUrl);
      console.log('✅ Logo uploadé avec succès en base de données');
      
    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      setError('Erreur lors de l\'upload du logo');
    } finally {
      setIsUploading(false);
      // Réinitialiser l'input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    if (!currentLogo) return;

    setIsUploading(true);
    setError(null);

    try {
      // Suppression directe en base de données
      const success = await AgencyService.removeAgencyLogo(agencyId);
      
      if (success) {
        onLogoUpdate(null);
        setPreview(null);
        console.log('✅ Logo supprimé avec succès');
      } else {
        setError('Erreur lors de la suppression du logo');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du logo:', error);
      setError('Erreur lors de la suppression du logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="logo-upload-container">
      <div className="flex flex-col space-y-4">
        {/* Aperçu du logo */}
        <div className="flex items-center space-x-4">
          <div 
            className={`
              relative w-24 h-24 border-2 border-dashed rounded-lg 
              flex items-center justify-center cursor-pointer
              transition-colors duration-200
              ${disabled ? 'border-gray-200 cursor-not-allowed' : 'border-gray-300 hover:border-blue-400'}
              ${isUploading ? 'opacity-50' : ''}
            `}
            onClick={handleClick}
          >
            {preview ? (
              <>
                <img 
                  src={preview} 
                  alt="Logo de l'agence" 
                  className="w-full h-full object-contain rounded-lg"
                />
                {!disabled && !isUploading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveLogo();
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    title="Supprimer le logo"
                  >
                    <FaTimes size={12} />
                  </button>
                )}
              </>
            ) : (
              <div className="text-center text-gray-400">
                <FaImage size={32} />
                <span className="text-xs mt-1 block">Logo</span>
              </div>
            )}
          </div>

          {/* Informations */}
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-700 mb-1">
              Logo de l'agence
            </div>
            <div className="text-xs text-gray-500 mb-2">
              Formats: {DEFAULT_LOGO_CONFIG.allowedFormats.join(', ')}
            </div>
            <div className="text-xs text-gray-500 mb-3">
              Taille max: {DEFAULT_LOGO_CONFIG.maxSizeKB}KB
            </div>
            <div className="text-xs text-gray-500 mb-2">
              Optimisé pour: 32×64px (header)
            </div>
            <div className="text-xs text-green-600">
              ⚡ Compression automatique inclus
            </div>
          </div>
        </div>

        {/* Input file caché */}
        <input
          ref={fileInputRef}
          type="file"
          accept={DEFAULT_LOGO_CONFIG.allowedFormats.map(fmt => `.${fmt}`).join(',')}
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
          className="hidden"
        />

        {/* Bouton d'upload */}
        {!preview && !disabled && (
          <button
            onClick={handleClick}
            disabled={isUploading}
            className={`
              inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md
              text-sm font-medium text-gray-700 bg-white
              hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              transition-colors duration-200
              ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <FaPlus />
            <span>{isUploading ? 'Upload en cours...' : 'Ajouter un logo'}</span>
          </button>
        )}

        {/* Erreur */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            {error}
          </div>
        )}

        {/* Indicateur de chargement */}
        {isUploading && (
          <div className="text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md p-3">
            Upload en cours...
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoUpload;
